import StatusPill from "@/components/StatusPill";

function fmtWhen(iso) {
  if (!iso) return "Never";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "Never";
  return d.toLocaleString(undefined, {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

/**
 * Standard agent card — used on Overview + AgentRoster.
 * Shows an explicit LAST RUN and SCHEDULE labeled block so the reader
 * can find those two fields without hunting through the summary.
 * `comingSoon=true` renders a grayed-out placeholder card.
 */
export default function AgentCard({ agent, onClick, comingSoon = false }) {
  if (comingSoon) {
    return (
      <div
        data-testid={`agent-card-coming-soon-${agent.id}`}
        className="text-left bg-white border border-dashed border-[#dbe0e8] rounded-xl p-4 flex items-start gap-3 opacity-70 cursor-default"
      >
        <div className="w-11 h-11 rounded-lg flex items-center justify-center bg-[#eef1f6] text-[color:var(--cc-ink-muted)] font-display font-bold text-[15px] shrink-0">
          ?
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="font-display font-semibold text-[15px] text-[#5c6877] truncate">{agent.name}</div>
            <span className="text-[10px] font-mono-data uppercase tracking-widest bg-[#fff6dc] text-[#8a6a1c] border border-[#f0dfa5] rounded px-1.5 py-0.5 font-bold whitespace-nowrap">
              Coming Soon
            </span>
          </div>
          <div className="text-[12px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mt-0.5">
            {agent.function}
          </div>
          {agent.description && (
            <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] mt-2 line-clamp-2">
              {agent.description}
            </div>
          )}
        </div>
      </div>
    );
  }

  const status = agent.status || (agent.latest_run && agent.latest_run.status) || "idle";
  const summary = agent.latest_run?.summary;
  const startedAt = agent.latest_run?.started_at;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={`agent-card-${agent.id}`}
      className="text-left bg-white border border-[#e7e9ee] rounded-xl p-4 hover:shadow-md hover:-translate-y-[1px] transition-all flex items-start gap-3 w-full"
    >
      <div
        className="w-11 h-11 rounded-lg flex items-center justify-center text-white font-display font-bold text-[15px] shrink-0"
        style={{ background: agent.avatar_color }}
      >
        {agent.initial}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="font-display font-semibold text-[15px] text-[#0e1420] truncate">{agent.name}</div>
          <span className="text-[10px] font-mono-data text-[#5c6877] uppercase tracking-widest whitespace-nowrap">{agent.agent_code}</span>
        </div>
        <div className="text-[12.5px] text-[#5c6877] mt-0.5 line-clamp-2">{agent.function}</div>
        <div className="mt-2.5"><StatusPill status={status} /></div>

        <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-[#eef1f6]">
          <div data-testid={`agent-card-lastrun-${agent.id}`}>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Last Run</div>
            <div className={`text-[12.5px] font-mono-data mt-0.5 font-semibold ${startedAt ? "text-[#0e1420]" : "text-[color:var(--cc-ink-muted)] italic font-normal"}`}>
              {fmtWhen(startedAt)}
            </div>
          </div>
          <div data-testid={`agent-card-schedule-${agent.id}`}>
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">Schedule</div>
            <div className="text-[12.5px] font-mono-data mt-0.5 font-semibold text-[#0e1420]">{agent.cadence || "—"}</div>
          </div>
        </div>

        {summary && (
          <div className="mt-2.5 text-[11.5px] text-[#485563] bg-[#f5f6f8] rounded-md px-2 py-1.5 border border-[#eef0f4]">
            {summary}
          </div>
        )}
      </div>
    </button>
  );
}
